import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';

import { CookieConsentStore } from '@arya/gdpr/cookie-consent/data-access';
import type { OptionalCookieCategories } from '@arya/gdpr/cookie-consent/domain';
import { CookieConsentBanner, CookieConsentPreferencesDialog } from '@arya/gdpr/cookie-consent/ui';

import { GoogleAnalyticsLoader } from '../google-analytics-loader';

@Component({
  selector: 'lib-cookie-consent-mount',
  imports: [CookieConsentBanner, CookieConsentPreferencesDialog],
  templateUrl: './cookie-consent-mount.html',
  styleUrl: './cookie-consent-mount.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CookieConsentMount {
  private readonly store = inject(CookieConsentStore);
  private readonly analytics = inject(GoogleAnalyticsLoader);
  private readonly dismissed = signal(false);

  protected readonly bannerVisible = computed(() => this.store.needsConsent() && !this.dismissed());
  protected readonly preferencesOpen = this.store.preferencesOpen;
  protected readonly current = computed<OptionalCookieCategories>(() => {
    const { functional, analytics, marketing } = this.store.currentCategories();
    return { functional, analytics, marketing };
  });

  constructor() {
    effect(() => {
      if (!this.store.loaded()) return;
      if (this.store.category('analytics')()) {
        this.analytics.enable();
      } else {
        this.analytics.disable();
      }
    });
  }

  protected onAcceptAll(): void {
    this.record({ functional: true, analytics: true, marketing: true });
  }

  protected onRejectAll(): void {
    this.record({ functional: false, analytics: false, marketing: false });
  }

  protected record(categories: OptionalCookieCategories): void {
    this.dismissed.set(true);
    void this.store.record(categories);
  }

  protected closePreferences(): void {
    this.store.closePreferences();
  }
}
